/**
 * useStoreSubscriptions — 启动时绑定 WS event → store action 的中转 composable
 *
 * 在 App.vue setup 内调用一次即可
 * - 监听共享 wsUiState → pushStore + machineStore.online（跨 reset 仍有效）
 * - 订阅通配符事件 → pushStore.dispatch（经 onKioskWsEnvelope，跨 reset）
 */

import { watch, onScopeDispose } from 'vue'
import {
  useKioskWs,
  onKioskWsEnvelope,
  getKioskWsUiState,
} from '@/composables/useKioskWs'
import { usePushStore } from '@/stores/pushStore'
import { useMachineStore } from '@/stores/machineStore'
import { getEffectiveMachineCodeSync } from '@/utils/machineCode'
import type { Envelope } from '@/ws/types'
import { logger } from '@/utils/logger'

export function useStoreSubscriptions(): void {
  // 创建/取得单例，但不在此自动 connect（等 App 写入 machine_code）
  useKioskWs({ autoConnect: false })
  const pushStore = usePushStore()
  const machineStore = useMachineStore()
  const wsUiState = getKioskWsUiState()

  const stopWatch = watch(
    wsUiState,
    (s) => {
      pushStore.setConnectionState(s)
      machineStore.setOnline(s === 'CONNECTED')
      logger.info('[useStoreSubscriptions] WS 状态同步', { state: s })

      if (s === 'CONNECTED' && !machineStore.code) {
        const lsKey = 'kiosk_machine_code'
        if (!localStorage.getItem(lsKey)) {
          const fallback = getEffectiveMachineCodeSync()
          localStorage.setItem(lsKey, fallback)
          logger.info('[useStoreSubscriptions] 写入 fallback machine_code', { code: fallback })
        }
        machineStore.refreshProfile().catch((err) => {
          logger.warn('[useStoreSubscriptions] refreshProfile 失败', { error: String(err) })
        })
      }

      if (s === 'CONNECTED') {
        import('@/composables/useApi').then(({ useApi }) => {
          useApi()
            .post('/api/kiosk/heartbeat', {
              app_version: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0',
            })
            .catch(() => {})
        })
      }
    },
    { immediate: true },
  )

  const off = onKioskWsEnvelope((envelope: Envelope) => {
    void pushStore.dispatch(envelope)
  })

  onScopeDispose(() => {
    stopWatch()
    off()
    logger.info('[useStoreSubscriptions] WS 订阅已清除')
  })
}
